import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { AlertCircle, BadgeCheck, Copy, Lightbulb, Loader2, Lock, MapPin, MousePointerClick, Send, ShieldCheck, UserCog } from 'lucide-react';
import { BackToAreaRiservata } from './CircolariPage';
import SchedaBadge from '../components/badge/SchedaBadge';
import VideoGuidaFinestra from '../components/support/guide/VideoGuidaFinestra';
import { getBadge, type BadgeAssociato } from '../services/badgeService';

/** Video guida «I badge UNASCA» su YouTube (1:46) */
export const VIDEO_GUIDA_BADGE_ID = 'oqv6-2EheuA';

const CARD = 'rounded-2xl bg-white dark:bg-gray-900 shadow-sm ring-1 ring-black/5 dark:ring-white/10';

const PASSI = [
  { Icona: MousePointerClick, titolo: 'Scegli il badge', testo: 'Formato orizzontale, quadrato o compatto; sfondo chiaro o scuro, in base ai colori del tuo sito.' },
  { Icona: Copy, titolo: 'Copia il codice', testo: 'Premi «Copia codice»: è già personalizzato per il tuo studio, non serve cambiare nulla.' },
  { Icona: Send, titolo: 'Incollalo nel sito', testo: 'Nel punto in cui vuoi che compaia, di solito il piè di pagina. Oppure giralo al tuo webmaster.' }
];

const GUIDA = [
  { titolo: 'WordPress', testo: "Nell'editor di una pagina o in «Aspetto» → «Widget» aggiungi un blocco «HTML personalizzato» e incolla il codice. Per averlo su tutte le pagine, mettilo nel widget del piè di pagina." },
  { titolo: 'Wix', testo: "Nell'editor scegli «Aggiungi elementi» → «Incorpora codice» → «Incorpora HTML», incolla il codice e posiziona il riquadro dove preferisci." },
  { titolo: 'Squarespace', testo: 'Aggiungi un blocco «Codice» (Code) nella pagina o nel footer e incolla il codice.' },
  { titolo: 'Joomla e altri siti', testo: 'Usa un modulo «HTML personalizzato» o il campo per il codice HTML del tuo sistema. Su un sito fatto su misura, il codice va nel file del piè di pagina.' }
];

const CONSIGLI = [
  'Il posto migliore è il piè di pagina, così il badge compare su tutte le pagine. Va bene anche la pagina «Chi siamo» o «Contatti».',
  'Non modificare il codice: contiene il collegamento giusto (per la Rete Certificata, alla scheda del tuo studio sulla mappa).',
  "Meglio il codice dell'immagine scaricata: l'immagine resta ospitata su studi.unasca.it ed è sempre aggiornata.",
  'Hai più sedi nella Rete Certificata? Ogni sede ha il suo badge: mettilo sulla pagina della sede giusta.',
  'Il badge è personale: usalo solo sul sito del tuo studio.'
];

/**
 * «I tuoi badge» (/area-riservata/badge): badge da inserire nel proprio sito, con il
 * codice già personalizzato. Badge «Associato» per chi è in regola; badge della Rete
 * per ogni scheda dello studio sulla mappa pubblica.
 */
const BadgeAssociatoPage: React.FC = () => {
  const navigate = useNavigate();
  const [dati, setDati] = useState<BadgeAssociato | null>(null);
  const [errore, setErrore] = useState(false);

  useEffect(() => {
    getBadge().then(setDati).catch(() => setErrore(true));
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <Helmet>
        <title>I tuoi badge — Area riservata UNASCA Studi</title>
      </Helmet>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
        <BackToAreaRiservata />

        <header className="mb-6 flex items-start gap-4">
          <span className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl bg-primary-700 text-white shadow">
            <BadgeCheck className="h-6 w-6" aria-hidden="true" />
          </span>
          <div>
            <h1 className="text-xl md:text-2xl font-bold tracking-tight text-gray-900 dark:text-white">I tuoi badge</h1>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400 max-w-2xl">
              Mostra sul tuo sito che fai parte di UNASCA. Scegli formato e sfondo, copia il codice e incollalo nel tuo sito:
              il badge resta sempre aggiornato e porta chi ci clicca sopra alla pagina giusta.
            </p>
            <div className="mt-3">
              <VideoGuidaFinestra videoId={VIDEO_GUIDA_BADGE_ID} titolo="Video guida ai badge UNASCA" durata="2 minuti" />
            </div>
          </div>
        </header>

        <ol className="mb-6 grid gap-3 sm:grid-cols-3">
          {PASSI.map(({ Icona, titolo, testo }, i) => (
            <li key={titolo} className={`${CARD} flex gap-3 p-4`}>
              <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300">
                <Icona className="h-4 w-4" aria-hidden="true" />
              </span>
              <div>
                <p className="text-sm font-semibold text-gray-900 dark:text-white"><span className="font-sans">{i + 1}.</span> {titolo}</p>
                <p className="mt-0.5 text-sm text-gray-600 dark:text-gray-400">{testo}</p>
              </div>
            </li>
          ))}
        </ol>

        <aside className="mb-8 flex flex-col sm:flex-row gap-4 rounded-2xl bg-amber-50 p-5 ring-1 ring-amber-200 dark:bg-amber-900/20 dark:ring-amber-800/60">
          <UserCog className="h-7 w-7 flex-shrink-0 text-amber-600 dark:text-amber-400" aria-hidden="true" />
          <div>
            <h2 className="text-base font-semibold text-gray-900 dark:text-white">Non sei pratico di siti web? Giralo al tuo webmaster</h2>
            <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">
              Non devi fare nulla di tecnico: scegli il badge che ti piace e premi <strong>«Invia al webmaster»</strong>.
              Si apre un'email già pronta con le istruzioni e il codice: basta aggiungere l'indirizzo di chi cura il tuo
              sito (l'agenzia web, il tecnico o il collaboratore che lo gestisce) e inviarla. In alternativa premi
              «Copia codice» e incollalo in un messaggio, per esempio su WhatsApp. Chiedigli di inserirlo <strong>senza
              modificarlo</strong>.
            </p>
          </div>
        </aside>

        {!dati && !errore && (
          <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 py-10 justify-center">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Caricamento dei badge…
          </div>
        )}

        {errore && (
          <div role="alert" className={`${CARD} flex items-center gap-3 p-5 text-sm text-red-700 dark:text-red-300`}>
            <AlertCircle className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
            Non è stato possibile caricare i tuoi badge. Riprova tra qualche minuto.
          </div>
        )}

        {dati && !dati.inRegola && (
          <div role="alert" className={`${CARD} text-center px-6 py-14`}>
            <span className="w-12 h-12 rounded-full mx-auto mb-4 flex items-center justify-center bg-amber-100 dark:bg-amber-900/40">
              <Lock className="w-5 h-5 text-amber-600 dark:text-amber-400" aria-hidden="true" />
            </span>
            <h2 className="text-base font-semibold text-gray-900 dark:text-white">I badge sono riservati agli associati in regola con la quota</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-2 max-w-md mx-auto">
              Rinnova la quota associativa per ottenere il badge da mettere sul tuo sito. Nel tuo profilo trovi lo stato del tesseramento.
            </p>
            <button
              type="button"
              onClick={() => navigate('/area-riservata?tab=profilo')}
              className="inline-flex items-center gap-1.5 mt-6 px-4 py-2 rounded-xl bg-primary-700 hover:bg-primary-800 text-white text-sm font-semibold transition-colors"
            >
              Vai al mio profilo
            </button>
          </div>
        )}

        {dati && dati.inRegola && (
          <div className="space-y-8">
            <section aria-labelledby="badge-associato" className="space-y-3">
              <h2 id="badge-associato" className="text-sm font-semibold uppercase tracking-wider text-primary-700 dark:text-primary-400">
                Associato UNASCA
              </h2>
              <SchedaBadge famiglia="associato" />
            </section>

            <section aria-labelledby="badge-rete" className="space-y-3">
              <h2 id="badge-rete" className="text-sm font-semibold uppercase tracking-wider text-primary-700 dark:text-primary-400">
                Rete Certificata
              </h2>
              {dati.reteCertificata.length > 0 ? (
                dati.reteCertificata.map((s) => (
                  <SchedaBadge key={s.idScheda} famiglia="rete-certificata" idScheda={s.idScheda} nome={s.nome} />
                ))
              ) : dati.certificatoSenzaScheda ? (
                <div className={`${CARD} flex items-start gap-3 p-5`}>
                  <MapPin className="h-5 w-5 flex-shrink-0 text-amber-600" aria-hidden="true" />
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    Il tuo certificato è attivo, ma la scheda del tuo studio non è ancora visibile sulla mappa della Rete
                    Certificata. Scrivi a{' '}
                    <a href="mailto:retecertificata@unasca.it" className="font-semibold text-primary-700 underline underline-offset-2 dark:text-primary-400">
                      retecertificata@unasca.it
                    </a>{' '}
                    per sistemarla: appena è sulla mappa trovi qui il tuo badge.
                  </p>
                </div>
              ) : (
                <div className={`${CARD} flex flex-col sm:flex-row sm:items-center gap-4 p-5`}>
                  <ShieldCheck className="h-8 w-8 flex-shrink-0 text-primary-700 dark:text-primary-400" aria-hidden="true" />
                  <p className="flex-1 text-sm text-gray-700 dark:text-gray-300">
                    Il badge della Rete Certificata è per gli studi certificati: porta i clienti direttamente alla scheda del
                    tuo studio sulla mappa.
                  </p>
                  <Link
                    to="/rete-certificata/studi"
                    className="inline-flex flex-shrink-0 items-center justify-center rounded-xl bg-primary-700 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-800 transition-colors"
                  >
                    Entra nella Rete Certificata
                  </Link>
                </div>
              )}
            </section>

            <section aria-labelledby="come-inserirlo" className={`${CARD} p-5 sm:p-6`}>
              <h2 id="come-inserirlo" className="text-base font-semibold text-gray-900 dark:text-white mb-1">Come inserirlo nel tuo sito</h2>
              <p className="mb-4 text-sm text-gray-600 dark:text-gray-400">Le istruzioni per le piattaforme più diffuse. Se il sito lo gestisce qualcun altro, usa «Invia al webmaster».</p>
              <dl className="grid gap-4 sm:grid-cols-2">
                {GUIDA.map((g) => (
                  <div key={g.titolo}>
                    <dt className="text-sm font-semibold text-gray-900 dark:text-white">{g.titolo}</dt>
                    <dd className="mt-1 text-sm text-gray-600 dark:text-gray-400">{g.testo}</dd>
                  </div>
                ))}
              </dl>

              <h3 className="mt-6 mb-3 flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-white">
                <Lightbulb className="h-4 w-4 text-amber-500" aria-hidden="true" /> Consigli
              </h3>
              <ul className="space-y-2">
                {CONSIGLI.map((c) => (
                  <li key={c} className="flex gap-2 text-sm text-gray-600 dark:text-gray-400">
                    <span className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-primary-500" aria-hidden="true" />
                    {c}
                  </li>
                ))}
              </ul>

              <p className="mt-5 text-xs text-gray-500 dark:text-gray-400">
                Altri materiali (loghi, testi, foto) sono nel{' '}
                <Link to="/press-kit" className="font-semibold text-primary-700 underline underline-offset-2 dark:text-primary-400">Press Kit</Link>.
              </p>
            </section>
          </div>
        )}
      </div>
    </div>
  );
};

export default BadgeAssociatoPage;
